/**
 * AdminProtectedRoute - Route Guard Component
 * 
 * Checks authentication and admin group membership
 * Supports requireSuperAdmin prop for elevated-access routes
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from './useAdminAuth.js';
import './AdminProtectedRoute.css';

export const AdminProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, isAuthenticated, isLoading } = useAdminAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="admin-protected-route__loading">
        <div className="admin-spinner"></div>
        <p>Verifying access...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has admin access
  if (!user?.groups?.includes('Admins') && !user?.groups?.includes('SuperAdmins')) {
    return (
      <div className="admin-protected-route__denied">
        <div className="admin-protected-route__denied-icon">🚫</div>
        <h1 className="admin-protected-route__denied-title">Access Denied</h1>
        <p className="admin-protected-route__denied-text">
          You do not have the required permissions to access this area.
        </p>
        <p className="admin-protected-route__denied-details">
          Admin or SuperAdmin group membership is required.
        </p>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => window.location.href = '/login'}
        >
          Return to Login
        </button>
      </div>
    );
  }

  // Check SuperAdmin requirement if specified
  if (requireSuperAdmin && !user?.isSuperAdmin) {
    return (
      <div className="admin-protected-route__denied">
        <div className="admin-protected-route__denied-icon">🔒</div>
        <h1 className="admin-protected-route__denied-title">SuperAdmin Access Required</h1>
        <p className="admin-protected-route__denied-text">
          This feature requires SuperAdmin privileges.
        </p>
        <p className="admin-protected-route__denied-details">
          Your account: <strong>{user?.email}</strong>
          <br />
          Current role: <strong>{user?.isAdmin ? 'Admin' : 'User'}</strong>
        </p>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={() => window.history.back()}
        >
          Go Back
        </button>
      </div>
    );
  }

  // User has required access, render children
  return <>{children}</>;
};

export default AdminProtectedRoute;
